import { useState, useEffect } from 'react';
import { Card, Modal, Nav } from 'react-bootstrap';
import { CreditCard, Plus, Trash2, FileText, Download, Edit2 } from 'lucide-react';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '../../../../contexts/AuthContext';
import { API_ENDPOINTS, STRIPE_PUBLISHABLE_KEY } from "../../../../config/api.config";
import { apiService } from "../../../../services/apiService";
import { PaymentMethodForm, BillingAddressForm } from '../../../../components/billing';
import { loadStripe } from "@stripe/stripe-js";
import EditPaymentMethodForm from '../../../../components/billing/EditPaymentMethodForm';
import StripeSecurityBadge from '../../../../components/billing/StripeSecurityBadge';
import {InvoiceResponse, PaymentMethodResponse, PaymentUpdateResponse} from "../../../../types";
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface AddedByUser {
    userId: string;
    email: string;
    name: string;
}

interface InvoiceDownloadResponse {
    url: string;
}

interface Payment {
    id: string;
    type: 'card';
    card: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    };
    billing_details?: {
        name?: string;
        address?: {
            line1?: string;
            line2?: string;
            city?: string;
            state?: string;
            postal_code?: string;
            country?: string;
        };
    };
    isDefault: boolean;
    addedBy: AddedByUser;
}
interface Invoice {
    id: string;
    number: string;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    created: string;
    period_start: number;
    period_end: number;
    status: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';
    hosted_invoice_url: string;
    subscription_id: string;
    payment_intent_id: string;
}

interface PlanFeatures {
    storage_limit_gb: number;
    max_kb_count: number;
    max_queries_per_day: number;
    conversations_limit: number;
    files_limit: number;
    data_sources_limit: number;
    has_api_access: boolean;
    has_web_crawling: boolean;
    has_api_connectors: boolean;
    support_level: string;
    advanced_features: boolean;
}

interface Plan {
    id: string;
    name: string;
    description: string;
    price: number;
    billing_interval: 'monthly' | 'yearly';
    trial_period_days: number | null;
    features: PlanFeatures;
}

interface SubscriptionPlan {
    id: string;
    name: string;
    description: string;
    price: number;
    billing_interval: 'monthly' | 'yearly';
    trial_period_days: number | null;
    features: PlanFeatures;
}

interface Subscription {
    id: string;
    status: 'active' | 'past_due' | 'canceled' | 'incomplete';
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    plan: SubscriptionPlan;
}

interface BillingDetails {
    name: string;
    address: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    };
}

const PaymentMethodsPage = () => {
    const auth = useAuth();
    const [paymentMethods, setPaymentMethods] = useState<Payment[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [invoicesLoading, setInvoicesLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [activeTab, setActiveTab] = useState('payment-methods');
    const [selectedNewPlan] = useState<Plan | null>(null);
    const [subscription] = useState<Subscription | null>(null);
    const [editingPaymentMethod, setEditingPaymentMethod] = useState<Payment | null>(null);
    const [showEditForm, setShowEditForm] = useState(false);

    useEffect(() => {
        if (activeTab === 'payment-methods') {
            fetchPaymentMethods();
        } else if (activeTab === 'invoices') {
            console.log('Fetching invoices from:', API_ENDPOINTS.BILLING.INVOICES.LIST);
            fetchInvoices();
        }
    }, [activeTab]);


    const fetchPaymentMethods = async () => {
        setLoading(true);
        try {
            // Debug token state
            const accessToken = localStorage.getItem('access_token');
            const auth0Token = localStorage.getItem('auth0_access_token');

            console.log('Token Debug:', {
                hasAccessToken: !!accessToken,
                hasAuth0Token: !!auth0Token,
                accessTokenPrefix: accessToken?.substring(0, 20),
                auth0TokenPrefix: auth0Token?.substring(0, 20),
                endpoint: API_ENDPOINTS.BILLING.PAYMENT_METHODS.LIST
            });

            console.log('Fetching payment methods...');
            const response = await apiService.get<PaymentMethodResponse>(
                API_ENDPOINTS.BILLING.PAYMENT_METHODS.LIST
            );

            if (Array.isArray(response.data)) {
                setPaymentMethods(response.data);
            } else {
                console.error('Unexpected response format:', response);
                setPaymentMethods([]);
            }
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            setError('Failed to load payment methods');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        setInvoicesLoading(true);
        try {
            console.log('Making request to:', API_ENDPOINTS.BILLING.INVOICES.LIST);
            const response = await apiService.get<InvoiceResponse>(API_ENDPOINTS.BILLING.INVOICES.LIST);
            console.log('Invoices response:', response);

            if (response?.data?.data) {
                console.log('Setting invoices:', response.data.data);
                setInvoices(response.data.data);
                setError(null);
            } else {
                console.log('No invoice data found in response');
                setInvoices([]);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load invoices';
            console.error('Error fetching invoices:', { error: err, message: errorMessage });
            setError(errorMessage);
            setInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    };

    const handleAddPaymentMethod = async (paymentMethodId: string) => {
        if (!auth?.user?.id) {
            setError('User not authenticated');
            return;
        }

        setProcessing(true);
        try {
            await apiService.post(API_ENDPOINTS.BILLING.PAYMENT_METHODS.ADD, {
                paymentMethodId,
                userId: auth.user.id
            });
            await fetchPaymentMethods();
            setShowAddForm(false);
        } catch (err) {
            setError('Failed to add payment method');
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleUpdatePaymentMethod = async (paymentMethodId: string, billingDetails: BillingDetails) => {
        setProcessing(true);
        try {
            const response = await apiService.post<PaymentUpdateResponse>(
                `${API_ENDPOINTS.BILLING.PAYMENT_METHODS.UPDATE}/${paymentMethodId}`,
                { billing_details: billingDetails }
            );
            setError(null);
            return response;
        } catch (err) {
            throw err;
        } finally {
            setProcessing(false);
        }
    };

    const handleSetDefault = async (paymentMethodId: string) => {
        setProcessing(true);
        try {
            await apiService.post(API_ENDPOINTS.BILLING.PAYMENT_METHODS.SET_DEFAULT, {
                paymentMethodId
            });
            await fetchPaymentMethods();
        } catch (err) {
            setError('Failed to set default payment method');
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (paymentMethodId: string) => {
        setProcessing(true);
        try {
            await apiService.delete(`${API_ENDPOINTS.BILLING.PAYMENT_METHODS.DELETE}/${paymentMethodId}`);
            await fetchPaymentMethods();
            setShowConfirmDelete(null);
        } catch (err) {
            setError('Failed to delete payment method');
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleDownloadInvoice = async (invoice: Invoice) => {
        try {
            console.log('Fetching invoice download URL:', invoice.id);
            const response = await apiService.get<InvoiceDownloadResponse>(
                `${API_ENDPOINTS.BILLING.INVOICES.DOWNLOAD}/${invoice.id}`
            );
            console.log('Download response:', response);

            if (response?.url) {
                window.open(response.url, '_blank');
            } else if (invoice.hosted_invoice_url) {
                window.open(invoice.hosted_invoice_url, '_blank');
            } else {
                throw new Error('No download URL available');
            }
        } catch (err) {
            console.error('Download error:', err);
            setError(err instanceof Error ? err.message : 'Failed to download invoice');
        }
    };

    const DeleteConfirmationModal = () => (
        <Modal show={!!showConfirmDelete} onHide={() => setShowConfirmDelete(null)}>
            <Modal.Header closeButton>
                <Modal.Title>Delete Payment Method</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Are you sure you want to delete this payment method?
            </Modal.Body>
            <Modal.Footer>
                <button
                    className="btn btn-secondary"
                    onClick={() => setShowConfirmDelete(null)}
                    disabled={processing}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-danger"
                    onClick={() => showConfirmDelete && handleDelete(showConfirmDelete)}
                    disabled={processing}
                >
                    {processing ? 'Deleting...' : 'Delete'}
                </button>
            </Modal.Footer>
        </Modal>
    );

    const AddPaymentMethodModal = () => (
        <Modal show={showAddForm} onHide={() => setShowAddForm(false)} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Add Payment Method</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Elements stripe={stripePromise}>
                    <PaymentMethodForm
                        onSuccess={handleAddPaymentMethod}
                        buttonText="Add Payment Method"
                    />
                </Elements>
            </Modal.Body>
        </Modal>
    );

    const EditPaymentMethodModal = () => (
        <Modal
            show={showEditForm}
            onHide={() => {
                setShowEditForm(false);
                setEditingPaymentMethod(null);
            }}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>Edit Payment Method</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {editingPaymentMethod && (
                    <EditPaymentMethodForm
                        paymentMethod={editingPaymentMethod}
                        onUpdate={async (paymentMethodId, billingDetails) => {
                            try {
                                // Close modal immediately
                                setShowEditForm(false);
                                setEditingPaymentMethod(null);

                                // Then perform the update
                                await handleUpdatePaymentMethod(paymentMethodId, billingDetails);

                                // Refresh payment methods list after successful update
                                await fetchPaymentMethods();
                            } catch (err) {
                                console.error('Error updating payment method:', err);
                                setError('Failed to update payment method');
                            }
                        }}
                    />
                )}
            </Modal.Body>
        </Modal>
    );

    const DowngradeModal = () => {
        if (!selectedNewPlan || !subscription) return null;
        return (
            <Modal show={showDowngradeModal} onHide={() => setShowDowngradeModal(false)}>
                {/* Rest of the modal code remains the same */}
            </Modal>
        );
    };

    const renderPaymentMethod = (method: Payment) => {
        const { card } = method;

        // Check if the current user is either an admin or in an admin group
        const isAdmin = auth.user?.role === 'admin' ||
            auth.user?.groups?.some(group => group.is_admin_group) ||
            false;

        // If the card was added by the current user or if it's a legacy card
        const isCardOwner = auth.user?.id === method.addedBy.userId ||
            (method.billing_details?.name === auth.user?.fullName && !method.addedBy.userId);

        // Add debugging logs
        console.log('Permission Debug:', {
            currentUserId: auth.user?.id,
            currentUserName: auth.user?.fullName,
            cardOwnerUserId: method.addedBy.userId,
            cardOwnerName: method.addedBy.name,
            billingDetailsName: method.billing_details?.name,
            isCardOwner,
            isAdmin,
            userRole: auth.user?.role,
            userGroups: auth.user?.groups,
            cardAddedBy: method.addedBy
        });

        // Can edit if you're the card owner OR if it's your name on a legacy card
        const canEdit = isCardOwner;

        return (
            <div
                key={method.id}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-start py-3"
            >
                <div>
                    <div className="d-flex align-items-center">
                        <CreditCard size={20} className="me-2 text-gray-600" />
                        <span className="text-capitalize text-gray-700">{card.brand}</span>
                        <span className="ms-2 text-gray-700">•••• {card.last4}</span>
                        <span className="ms-2 text-gray-600">
                        Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </span>
                        {method.isDefault && (
                            <span className="badge bg-primary ms-2">Default</span>
                        )}
                    </div>
                    <div className="ms-7 text-gray-600">
                        Added by {method.billing_details?.name || method.addedBy.name}
                        {(isAdmin || isCardOwner) && method.addedBy.email !== 'Unknown' && (
                            <span className="ms-2 text-gray-500">
                            ({method.addedBy.email})
                        </span>
                        )}
                    </div>
                </div>
                <div className="d-flex align-items-start">
                    {/* Both admins and card owners can set default */}
                    {!method.isDefault && (isAdmin || isCardOwner) && (
                        <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleSetDefault(method.id)}
                            disabled={processing}
                        >
                            Set as Default
                        </button>
                    )}

                    {/* Show edit/delete buttons for card owner */}
                    {canEdit ? (
                        <>
                            <button
                                className="btn btn-sm btn-outline-secondary me-2"
                                onClick={() => {
                                    setEditingPaymentMethod(method);
                                    setShowEditForm(true);
                                }}
                                disabled={processing}
                            >
                                <Edit2 size={16} />
                            </button>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setShowConfirmDelete(method.id)}
                                disabled={processing}
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    ) : (
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Only {method.addedBy.name} can modify this card
                    </span>
                    )}
                </div>
            </div>
        );
    };

    const renderPaymentMethodsTab = () => (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="d-flex align-items-center mb-2">
                                <CreditCard size={24} className="text-primary me-2" />
                                <h6 className="mb-0">Saved Payment Methods</h6>
                            </div>
                            <StripeSecurityBadge className="mt-2" />
                        </div>
                        <button
                            className="btn btn-primary d-flex align-items-center"
                            onClick={() => setShowAddForm(true)}
                        >
                            <Plus size={16} className="me-2" />
                            Add Payment Method
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : paymentMethods.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        No payment methods added yet
                    </div>
                ) : (
                    <div className="list-group">
                        {paymentMethods.map(renderPaymentMethod)}
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    const renderInvoicesTab = () => (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                    <FileText size={24} className="text-primary me-2" />
                    <h6 className="mb-0">Billing History</h6>
                </div>

                {invoicesLoading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : invoices && invoices.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                            <tr>
                                <th>Invoice Number</th>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Period</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {invoices.map((invoice) => (
                                <tr key={invoice.id}>
                                    <td>{invoice.number}</td>
                                    <td>{new Date(Number(invoice.created) * 1000).toLocaleDateString()}</td>
                                    <td>${(invoice.amount_due / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td>
                                        <span className={`badge bg-${
                                            invoice.status === 'paid' ? 'success' :
                                                invoice.status === 'open' ? 'warning' :
                                                    'danger'
                                        }`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(Number(invoice.period_start) * 1000).toLocaleDateString()} - {new Date(Number(invoice.period_end) * 1000).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleDownloadInvoice(invoice)}
                                        >
                                            <Download size={16} className="me-1" />
                                            View Invoice
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-4 text-muted">
                        No invoices available
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    if (!auth?.user) return null;

    return (
        <div className="page-container">
            <h5 className="mb-4">Payment & Billing</h5>
            {loading ? (
                <ContentSkeleton />
            ) : (
                <>
                    {error && (
                        <div className="alert alert-danger mb-4" role="alert">
                            {error}
                        </div>
                    )}

                    <Nav
                        variant="tabs"
                        className="mb-4"
                        activeKey={activeTab}
                        onSelect={(k) => k && setActiveTab(k)}
                    >
                        <Nav.Item>
                            <Nav.Link eventKey="payment-methods">Payment Methods</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="billing-address">Billing Address</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="invoices">Invoices</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {activeTab === 'payment-methods' ? (
                        renderPaymentMethodsTab()
                    ) : activeTab === 'billing-address' ? (
                        <BillingAddressForm />
                    ) : (
                        renderInvoicesTab()
                    )}

                    <DeleteConfirmationModal />
                    <AddPaymentMethodModal />
                    <DowngradeModal />
                    <EditPaymentMethodModal />
                </>
            )}
        </div>
    );
};

export default PaymentMethodsPage;